/**
 * Conectores en español que se mantienen en minúscula dentro de un título
 * (a menos que sean la primera palabra).
 */
const TITLE_CASE_LOWER_WORDS = new Set([
  "de", "del", "la", "las", "los", "el", "y", "e", "o", "u",
  "en", "a", "con", "por", "para", "al", "lo", "un", "una",
]);

/**
 * Normaliza un texto a "Primera Letra Mayúscula" (Title Case en español):
 * cada palabra con mayúscula inicial y el resto en minúscula, dejando los
 * conectores comunes (de, la, y, en…) en minúscula salvo la primera palabra.
 * Ej: "HOSTERÍA CABALLO CAMPANA" → "Hostería Caballo Campana",
 *     "CENTRO DE CONVENCIONES" → "Centro de Convenciones".
 * Pensado para campos de texto libre (lugar, ciudad, edad, accesibilidad).
 */
export function toTitleCase(input: string | null | undefined): string {
  if (!input) return "";
  let wordIndex = 0;
  return input
    .toLocaleLowerCase("es")
    .split(/(\s+)/)
    .map((token) => {
      if (token === "" || /^\s+$/.test(token)) return token;
      const isFirst = wordIndex === 0;
      wordIndex += 1;
      if (!isFirst && TITLE_CASE_LOWER_WORDS.has(token)) return token;
      return token.charAt(0).toLocaleUpperCase("es") + token.slice(1);
    })
    .join("");
}

const WEEKDAYS = [
  "lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo",
];

/** Minúsculas sin acentos, para comparar días sin importar tildes/mayúsculas. */
function stripAccentsLower(input: string): string {
  return input
    .toLocaleLowerCase("es")
    .replace(/[áàä]/g, "a")
    .replace(/[éèë]/g, "e")
    .replace(/[íìï]/g, "i")
    .replace(/[óòö]/g, "o")
    .replace(/[úùü]/g, "u");
}

/**
 * Filtra el horario de un evento a solo los días de las localidades elegidas.
 * El horario del evento trae varios tramos separados por "·"
 * (ej. "Viernes: 17h00 - 02h00 · Sábado: 11h00 - 02h00"). Según el día que
 * aparezca en el nombre de las localidades seleccionadas (ej. "GENERAL VIERNES
 * 18 DE SEPTIEMBRE"), devuelve solo el/los tramo(s) correspondiente(s).
 *
 * Es tolerante: si el horario no tiene varios tramos, o no se puede emparejar
 * ningún día, devuelve el horario completo sin cambios (no afecta a eventos que
 * no siguen esta convención).
 */
export function scheduleForLocalities(
  eventHour: string | null | undefined,
  localityNames: string[],
): string {
  const full = eventHour ?? "";
  if (!full) return "";

  const segments = full
    .split(/[·\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length < 2) return full;

  const selectedDays = new Set<string>();
  for (const name of localityNames) {
    const normalized = stripAccentsLower(name);
    for (const day of WEEKDAYS) {
      if (normalized.includes(day)) selectedDays.add(day);
    }
  }
  if (selectedDays.size === 0) return full;

  const matched = segments.filter((seg) => {
    const s = stripAccentsLower(seg);
    return [...selectedDays].some((day) => s.includes(day));
  });

  return matched.length > 0 ? matched.join(" · ") : full;
}

/**
 * Devuelve la descripción de una localidad solo si aporta información:
 * no vacía tras `trim` y distinta del nombre (sin distinguir mayúsculas).
 * En otro caso devuelve null. No normaliza tildes ni colapsa espacios internos.
 * Regla de presentación: NO aplicar en el mapper (mutaría Locality.description).
 */
export function visibleLocalityDescription(
  name: string | null | undefined,
  description: string | null | undefined,
): string | null {
  const desc = (description ?? "").trim();
  if (!desc) return null;
  const label = (name ?? "").trim();
  return desc.toLowerCase() === label.toLowerCase() ? null : desc;
}

/**
 * Formats a number as currency (USD).
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

/**
 * Formats an ISO 8601 date string to a locale-friendly display.
 * Accepts: "2025-09-18" or "2025-09-18T20:00:00" → "18 septiembre, 2025"
 * Backend always provides ISO 8601 (TKT-001 AC-11).
 */
export function formatDate(dateInput: string | Date | null): string {
  if (!dateInput) return "Fecha por confirmar";

  const date =
    dateInput instanceof Date ? dateInput : new Date(dateInput);

  if (isNaN(date.getTime())) return String(dateInput);

  const day = date.getUTCDate();
  const month = date.toLocaleDateString("es-EC", {
    month: "long",
    timeZone: "UTC",
  });
  const year = date.getUTCFullYear();
  return `${day} ${month}, ${year}`;
}

/**
 * Formats a date range for display.
 * Same month+year → "25 y 26 de abril, 2026"
 * Different month or year → "25 abril, 2026 - 3 mayo, 2026"
 */
export function formatDateRange(
  startInput: string | Date | null | undefined,
  endInput: string | Date | null | undefined
): string {
  if (!startInput) return "Fecha por confirmar";
  if (!endInput) return formatDate(startInput);

  const start =
    startInput instanceof Date ? startInput : new Date(startInput);
  const end = endInput instanceof Date ? endInput : new Date(endInput);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return `${formatDate(startInput)} - ${formatDate(endInput)}`;
  }

  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();

  if (sameMonth && sameYear) {
    const month = start.toLocaleDateString("es-EC", {
      month: "long",
      timeZone: "UTC",
    });
    const year = start.getUTCFullYear();
    return `${start.getUTCDate()} y ${end.getUTCDate()} de ${month}, ${year}`;
  }

  return `${formatDate(startInput)} - ${formatDate(endInput)}`;
}
